/**
 * SYNESTHESIA (the VJ app on STREAMPC), the pure half: what a pad, the grid and a timeline cue SAY, and how that goes
 * on the wire. OPBridge S344/S352 (their DEC-756, OPApps/opbridge-opbuttons.md "Synesthesia ... from OPButtons"):
 * OPButtons says WHAT (`scene {name}`, `next`, `meta {control, value}`), OPBridge turns it into MIDI today and OSC once
 * the operator has Synesthesia's Pro licence, with no change here.
 *
 * The wire is one body everywhere: `{module: "synesthesia", command, args}` as a timeline cue or a rule, or
 * `POST /synesthesia/fire {command, args}` from a press. `args.when` (`bar` | `beat` | `now`) is a press's choice; a
 * timeline cue sends none (it is already on its beat, so OPBridge plays it at once).
 *
 * Pure: no signals, no fetch. The list, the pictures and the press live in `list.ts` and `fire.ts`.
 */

import type { RuleAction } from '../opbridge/httpClient.ts';

/** When a press lands: OPBridge waits for it on its own clock (`bar` = the press default), at once while stopped. */
export type SynWhen = 'bar' | 'beat' | 'now';
export const SYN_WHENS: Array<[SynWhen, string]> = [['bar', 'Bar'], ['beat', 'Beat'], ['now', 'Now']];

export type SynCommand =
  | 'scene' | 'next' | 'prev' | 'random' | 'home'
  | 'playlistTimer' | 'playlistNext' | 'playlistPrev'
  | 'meta' | 'control';

/** One thing to tell Synesthesia: what a pad stores, what a cue carries (as {@link synRuleAction}). */
export interface SynAction {
  command: SynCommand;
  /** `scene`: the scene's name, exactly as Synesthesia names it. */
  scene?: string;
  /** `meta`: OPBridge's name for the control (`hue`, `brightness`, `invert`...). */
  control?: string;
  /** `meta` / `control`: 0..1 (a toggle is 0 or 1). */
  value?: number;
  /** `meta` / `control`: fade there over this many beats of the clock; absent or 0 = at once. */
  fadeBeats?: number;
  /** `control`: the playing scene's own control 1..8 (taught once by the operator in Synesthesia). */
  slot?: number;
}

/** Commands that CHANGE THE SCENE: drawn as a state on a timeline (the scene runs until the next change). */
const SCENE_CHANGES = new Set<SynCommand>(['scene', 'next', 'prev', 'random', 'home', 'playlistTimer', 'playlistNext', 'playlistPrev']);
export const isSceneChange = (a: Pick<SynAction, 'command'>): boolean => SCENE_CHANGES.has(a.command);
const ALL = new Set<SynCommand>([...SCENE_CHANGES, 'meta', 'control']);
export const isSynCommand = (c: unknown): c is SynCommand => typeof c === 'string' && ALL.has(c as SynCommand);

/** The moves a grid and a picker offer, in the order they sit. */
export const SYN_MOVES: Array<{ command: SynCommand; label: string; short: string; hint: string }> = [
  { command: 'prev', label: 'Previous scene', short: 'Prev', hint: 'the scene before the last one sent, in your scene order' },
  { command: 'random', label: 'Random scene', short: 'Random', hint: 'any of your scenes but the one playing' },
  { command: 'next', label: 'Next scene', short: 'Next', hint: 'the scene after the last one sent, in your scene order' },
  // OPBridge S352: Home is a control the operator MIDI-learns once in Synesthesia (OPBridge's Synesthesia panel, Teach);
  // until then a press does nothing there. The list does not say yet which are taught (asked of OPBridge).
  { command: 'home', label: 'Home playlist', short: 'Home', hint: "back to your home playlist (once Home is taught in OPBridge's Synesthesia panel)" },
];
/** Synesthesia's own playlist controls (S352: written by OPBridge on CC 51-53). */
export const SYN_PLAYLIST: Array<{ command: SynCommand; label: string; short: string; hint: string }> = [
  { command: 'playlistPrev', label: 'Playlist: previous', short: 'Prev', hint: "Synesthesia's playlist steps back one scene" },
  { command: 'playlistTimer', label: 'Playlist: timer', short: 'Timer', hint: "Synesthesia's playlist timer, on or off" },
  { command: 'playlistNext', label: 'Playlist: next', short: 'Next', hint: "Synesthesia's playlist steps on one scene" },
];

/** Plain names for OPBridge's meta controls; an unknown one is spelled out from its own name. */
const META_LABEL: Record<string, string> = {
  master: 'Master', brightness: 'Brightness', gamma: 'Gamma', contrast: 'Contrast', hue: 'Hue', saturation: 'Saturation',
  invert: 'Invert', vertMirror: 'Vertical mirror', horMirror: 'Horizontal mirror', limitColors: 'Limit colours',
};
export function metaLabel(name: string): string {
  if (META_LABEL[name]) return META_LABEL[name]!;
  const words = name.replace(/^_+/, '').replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim().toLowerCase();
  return words ? words[0]!.toUpperCase() + words.slice(1) : name;
}
/** The meta controls a build has when its list does not say (OPBridge S352's seven). */
export const DEFAULT_META: Array<{ name: string; type: string }> = [
  { name: 'master', type: 'slider' }, { name: 'brightness', type: 'slider' }, { name: 'gamma', type: 'slider' },
  { name: 'contrast', type: 'slider' }, { name: 'hue', type: 'slider' }, { name: 'saturation', type: 'slider' },
  { name: 'invert', type: 'toggle' },
];
/** Where a slider rests before anything is sent: Synesthesia's own defaults as far as they are known (master and
 *  brightness full, the rest centred), so a fresh slider does not jump the picture when first touched. */
export function metaRest(name: string, type: string): number {
  if (type === 'toggle') return 0;
  return name === 'master' || name === 'brightness' ? 1 : 0.5;
}

const clamp01 = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 1000) / 1000;
const pct = (v: number | undefined) => `${Math.round(clamp01(v ?? 0) * 100)}%`;
const beats = (n: number | undefined) => (n && n > 0 ? `, over ${+n.toFixed(2)} beat${n === 1 ? '' : 's'}` : '');

/** What it does, in a few words: "Boreal", "Next scene", "Hue to 40%, over 8 beats", "Invert on". */
export function synLabel(a: SynAction, metaType?: string): string {
  switch (a.command) {
    case 'scene': return a.scene?.trim() || 'No scene chosen';
    case 'meta': {
      const name = metaLabel(a.control ?? '');
      if (!a.control) return 'No control chosen';
      if (metaType === 'toggle') return `${name} ${(a.value ?? 0) >= 0.5 ? 'on' : 'off'}`;
      return `${name} to ${pct(a.value)}${beats(a.fadeBeats)}`;
    }
    case 'control': return `Scene control ${a.slot ?? '?'} to ${pct(a.value)}${beats(a.fadeBeats)}`;
    default: {
      const m = [...SYN_MOVES, ...SYN_PLAYLIST].find((x) => x.command === a.command);
      return m?.label ?? a.command;
    }
  }
}

/** The wire args for `a`, `when` added for a press (a cue sends none). Nothing the command does not take. */
export function synArgs(a: SynAction, when?: SynWhen): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (a.command === 'scene') out['name'] = a.scene ?? '';
  if (a.command === 'meta') { out['control'] = a.control ?? ''; out['value'] = clamp01(a.value ?? 0); }
  if (a.command === 'control') { out['slot'] = Math.max(1, Math.min(8, Math.round(a.slot ?? 1))); out['value'] = clamp01(a.value ?? 0); }
  if ((a.command === 'meta' || a.command === 'control') && a.fadeBeats && a.fadeBeats > 0) out['fadeBeats'] = Math.round(a.fadeBeats * 1000) / 1000;
  if (when) out['when'] = when;
  return out;
}

/** The command a build takes for `c`: S352 renamed the playlist's play to `playlistTimer`; an older OPBridge that
 *  lists only `playlistPlay` gets that. `commands` absent = trust `c`. */
export function wireCommand(c: SynCommand, commands?: readonly string[] | null): string {
  if (c === 'playlistTimer' && commands && !commands.includes('playlistTimer') && commands.includes('playlistPlay')) return 'playlistPlay';
  return c;
}
/** Whether that OPBridge takes `c` at all (its list says), so a control it cannot carry is not offered. */
export function takesCommand(c: SynCommand, commands?: readonly string[] | null): boolean {
  if (!commands || commands.length === 0) return true;
  return commands.includes(wireCommand(c, commands));
}

/** What is wrong with `a` before it is sent, or null. Said in the editor and on a pad's face. */
export function synProblem(a: SynAction | undefined): string | null {
  if (!a) return 'pick what it does';
  if (a.command === 'scene' && !a.scene?.trim()) return 'pick a scene';
  if (a.command === 'meta' && !a.control) return 'pick a control';
  return null;
}

/** The rules-engine action a timeline cue carries (no `when`: a cue is already on its beat). */
export function synRuleAction(a: SynAction, commands?: readonly string[] | null): RuleAction {
  return { module: 'synesthesia', command: wireCommand(a.command, commands), args: synArgs(a) } as RuleAction;
}

/** A cue's action read back as a Synesthesia action, or null when it is not one. Tolerant of what an older build or
 *  the lights kit wrote (`playlistPlay`, extra args). */
export function synActionOf(action: unknown): SynAction | null {
  const a = action as { module?: unknown; command?: unknown; args?: Record<string, unknown> } | null | undefined;
  if (!a || a.module !== 'synesthesia') return null;
  const command = a.command === 'playlistPlay' ? 'playlistTimer' : a.command;
  if (!isSynCommand(command)) return null;
  const args = a.args ?? {};
  const out: SynAction = { command };
  if (typeof args['name'] === 'string') out.scene = args['name'];
  if (typeof args['control'] === 'string') out.control = args['control'];
  if (typeof args['value'] === 'number') out.value = args['value'];
  if (typeof args['fadeBeats'] === 'number') out.fadeBeats = args['fadeBeats'];
  if (typeof args['slot'] === 'number') out.slot = args['slot'];
  return out;
}

/** The lights kit wrote its Synesthesia cues on a plain `mixed` lane named "Synesthesia" (CP-215, the four chorus
 *  loops): a lane whose every step is a Synesthesia cue is drawn and edited as a Synesthesia lane. */
export function isSynLaneByContent(steps: ReadonlyArray<{ kind: string; action?: unknown }>): boolean {
  return steps.length > 0 && steps.every((s) => s.kind === 'cue' && synActionOf(s.action) !== null);
}
